import React, { useContext, useEffect, useState } from 'react';
import { getProfileDataByDay } from '../../NecttosComp/APICaller/ApiServices';
import { NecttosContext } from '../../context/NecttosContext';
import { useQuery } from 'react-query';
import { Main } from '../../NecttosComp/Layout/Layout';
import Button from '../../NecttosComp/Button/Button';
import Input2 from '../../NecttosComp/Input/Input2';
import Instruction from '../../Documentation/Instruction';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { message } from 'antd';

interface AdmissionDataEditProps {
	userId: string;
	state: any;
	setState: any;
	onSubmit: any;
	onClose: any;
	TimerFunction: any;
	startDateTimer: any;
}

interface ImageUploadProps {
	studentId: string;
	onUploadSuccess?: (url: string) => void;
}


const CircularChart = ({ value = 0, color = 'text-blue-600', delay = 100 }) => {
	const radius = 18;
	const stroke = 4;
	const normalizedRadius = radius - stroke * 0.5;
	const circumference = normalizedRadius * 2 * Math.PI;

	const [animatedValue, setAnimatedValue] = useState<number>(0);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const duration = 1000;
			const startTime = performance.now();

			const animate = (time: number) => {
				const progress = Math.min((time - startTime) / duration, 1);
				const current = Math.floor(progress * Number(value || 0));
				setAnimatedValue(current);
				if (progress < 1) requestAnimationFrame(animate);
			};

			requestAnimationFrame(animate);
		}, delay);

		return () => clearTimeout(timeout);
	}, [value, delay]);

	const validAnimatedValue = isNaN(animatedValue) ? 0 : animatedValue;
	const offset = circumference - (validAnimatedValue / 100) * circumference;

	return (
		<svg viewBox={`0 0 ${radius * 2} ${radius * 2}`} className='w-16 h-16 transition-all duration-300'>
			<circle className='text-slate-200' stroke='currentColor' strokeWidth={stroke} fill='transparent' r={normalizedRadius} cx={radius} cy={radius} />
			<circle className={`${color} drop-shadow-sm transition-all duration-1000`} stroke='currentColor' strokeWidth={stroke} fill='transparent' r={normalizedRadius} cx={radius} cy={radius} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap='round' />
			<text x='50%' y='50%' textAnchor='middle' dominantBaseline='middle' className='fill-slate-800 dark:fill-white text-[10px] font-bold animate-pulse'>
				{validAnimatedValue}%
			</text>
		</svg>
	);
};

const AdmissionDataEdit: React.FC<AdmissionDataEditProps> = ({ userId, state, TimerFunction, startDateTimer, setState, onSubmit, onClose }) => {
	const { collegeId } = useContext(NecttosContext);
	const [isSaving, setIsSaving] = useState(false);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
	const [isUploading, setIsUploading] = useState<boolean>(false);

	const { data, isFetched } = useQuery(['getProfileDataTc', userId, collegeId], () => getProfileDataByDay({ userId, collegeId, days: 15 }), {
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	useEffect(() => {
		setState(data?.profile);
	}, [isFetched]);

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files ? event.target.files[0] : null;

		if (!file) {
			setSelectedFile(null);
			setImagePreview(undefined);
			return;
		}

		if (!file.type.startsWith('image/')) {
			message.error('Please select a valid image file.');
			return;
		}

		if (file.size > 2 * 1024 * 1024) {
			message.error('File size should be less than 5MB.');
			return;
		}

		setSelectedFile(file);

		const reader = new FileReader();
		reader.onloadend = () => {
			setImagePreview(reader.result as string);
		};
		reader.readAsDataURL(file);

		await uploadImageToFirebase(file);
	};

	const uploadImageToFirebase = async (file?: File): Promise<string | null> => {
		const fileToUpload = file || selectedFile;

		if (!fileToUpload) {
			message.error('No file selected.');
			return null;
		}

		setIsUploading(true);

		try {
			const storage = getStorage();
			const imageRef = ref(storage, `profilePhoto/${userId}/photoURL.png`);

			const snapshot = await uploadBytes(imageRef, fileToUpload, {
				contentType: fileToUpload.type,
			});

			const downloadURL = await getDownloadURL(snapshot.ref);

			setState((prevState: any) => ({
				...prevState,
				photoURL: downloadURL,
			}));

			message.success('Photo uploaded successfully!');

			return downloadURL;
		} catch (error) {
			console.error('Error uploading image:', error);
			message.error('Failed to upload image. Please try again.');
			return null;
		} finally {
			setIsUploading(false);
		}
	};


	const handleChangePhotoClick = () => {
		const fileInput = document.getElementById('photo-file-input') as HTMLInputElement;
		fileInput?.click();
	};

	const updateProfile = async (payload: any) => {
		setIsSaving(true);
		try {
			const instance = await getAxiosTokenInstance();
			const resp = await instance.post('/college/admissionProcess2/editupdateProfile', payload);
			if (!resp) {
				throw new Error('Failed to update profile');
			}
			const data = resp;

			console.log('Profile updated successfully');

			onClose();

			setTimeout(() => {
				window.location.reload();
			}, 300);

			return data;
		} catch (error) {
			console.error('Error updating profile:', error);
			setIsSaving(false);
			throw error;
		}
	};

	const total = data?.keysByCategory?.reduce((acc: number, item: any) => {
		return acc + (item.keys?.length || 0);
	}, 0);

	const completed = Object.keys(state ? state : {})?.length;
	const percent = Math.min((completed / total) * 100, 100);

	return (
		<Main height='100vh' width='100vw' title='Edit Student Data'>
			<div className='flex fixed'>
				<div className='w-[18vw]'>
					<div className='relative w-full'>
						{/* Hidden file input */}
						<input id='photo-file-input' type='file' accept='image/*' onChange={handleFileUpload} style={{ display: 'none' }} />

						<img src={imagePreview || state?.photoURL || data?.profile?.photoURL} className='w-full h-60 object-cover rounded-xl' alt='' />
						<button onClick={handleChangePhotoClick} disabled={isUploading} className='absolute bottom-2 right-2 px-3.5 py-2.5 rounded-md bg-orange-500 text-white text-xs font-semibold shadow hover:scale-105 transition-transform flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed'>
							<svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
								<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 17h2m2-2l6 6M2 12l6-6 6 6' />
							</svg>
							{isUploading ? 'Uploading...' : 'Change photo'}
						</button>
					</div>

					<h2 className='dark:text-slate-300 font-bold text-xl mt-2'>{data?.profile?.name}</h2>
					<h2 className='dark:text-slate-300 font-bold text-xl'>{data?.profile?.shortName}</h2>
					<h2 className='dark:text-slate-300 font-bold text-xl'>{data?.profile?.batchName}</h2>
					<h2 className='dark:text-slate-300 font-bold text-xl'>{data?.profile?.admisionNo}</h2>
				</div>

				<div className='w-[66vw] grid  grid-cols-2 h-[85vh] overflow-y-auto'>
					{data?.keysByCategory?.map((categ: any) => (
						<div className='w-[32vw] bg-slate-300 dark:bg-neutral-800 rounded-xl  m-2 p-2'>
							<h3 className='dark:text-slate-300 mx-6 my-6 text-xl '>{categ.title}</h3>
							{categ?.keys?.map((item: any) => {
								return (
									<div className='flex'>
										<Input2 {...item} className='w-[30vw]' fieldName={item?.shortName} type={item?.type === 'dropdown' ? 'drop' : item?.type === 'multi-select' ? 'mdrop' : item?.type} state={state} setState={setState} returnKey={item?._id} />
									</div>
								);
							})}
						</div>
					))}
				</div>

				<div className='w-[10vw]'>
					<Button width='93%' type='close' onClick={onClose}>
						Close
					</Button>
					<Instruction width='93%' keyId={'AdmissionDataEdit'} />
					<TimerFunction start={true} date={startDateTimer} className='text-xl font-mono w-[95%] text-center rounded-lg p-3 ml-1 h-[40px] flex justify-center items-center  dark:bg-neutral-700 dark:text-slate-100   bg-slate-100 text-slate-800' />
					<div className='flex justify-center items-center m-5 scale-150'>
						<CircularChart value={Math.round(percent)} color='text-emerald-600 dark:text-emerald-400' />
					</div>
					<Button width='93%' type='fetch' onClick={onClose}>
						Revert All Changes
					</Button>
					<Button
						width='93%'
						type='save'
						disabled={isSaving}
						onClick={() =>
							updateProfile({
								userId,
								collegeId,
								profile: state,
							})
						}>
						{isSaving ? 'Saving...' : 'Save All Data'}
					</Button>
				</div>
			</div>
		</Main>
	);
};

export default AdmissionDataEdit;
